/* Aurmina - derived price figures
   REVISION a
   Revision:      a - 26 Aug 2026
   Goes in:       assets/aurmina-derived-price.js
   Loaded by:     sections/aurmina-cost-tiles.liquid

   WHY THIS EXISTS. Section 5 of the project brief: Sami B2B Wholesale
   rewrites displayed prices client-side, after render, by finding a .price
   element inside [ref="priceContainer"]. Any figure Liquid derives from
   variant.price is therefore the RETAIL figure, and stays retail on the page
   for a logged-in wholesale customer even though the price above it has just
   changed. On this page that would mean a wholesale customer reading a
   wholesale bottle price and a retail cost-per-year directly beneath it.

   This is the same job aurmina-tier-savings.js does for the pricing tiers,
   written against a different section's markup. It is deliberately NOT the
   same file: that one recalculates savings badges and per-unit lines from
   compare-at prices, and its selectors are its own.

   WHAT IT DOES. For each [data-aurmina-derive] container it reads the
   rendered price text, divides by the supply period to get a yearly figure,
   divides that by twelve to get a monthly one, and writes both back into
   [data-aurmina-yearly] and [data-aurmina-monthly].

   FORMATTING. Rather than guess a currency format, it reuses the string the
   server already rendered into each target and swaps the numeric run inside
   it. "$233" becomes "$200"; "233 kr" becomes "200 kr". Grouping separators
   are re-derived from the price string's own separator, so a store running
   1.234,56 formatting stays consistent.

   NO JAVASCRIPT, NO PROBLEM. Liquid prints correct retail values first, so
   the tiles are right for every logged-out and retail visitor before this
   runs at all. This only ever corrects the wholesale case.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__aurminaDerivedPrice) return;
  window.__aurminaDerivedPrice = true;

  var SETTLE_MS = 120;
  var BACKSTOP_MS = 400;

  /* Pull a number out of a rendered money string. Handles 1,234.56 and
     1.234,56 by treating the LAST separator as the decimal point when it is
     followed by exactly two digits. */
  function parseMoney(text) {
    if (!text) return null;
    var raw = String(text).replace(/[^0-9.,]/g, '');
    if (!raw) return null;

    var lastDot = raw.lastIndexOf('.');
    var lastComma = raw.lastIndexOf(',');
    var cut = Math.max(lastDot, lastComma);
    var value;

    if (cut > -1 && raw.length - cut - 1 === 2) {
      value = raw.slice(0, cut).replace(/[.,]/g, '') + '.' + raw.slice(cut + 1);
    } else {
      value = raw.replace(/[.,]/g, '');
    }

    var n = parseFloat(value);
    return isNaN(n) ? null : n;
  }

  /* Which character this store uses to group thousands, taken from the price
     string itself so we do not impose one. */
  function groupChar(text) {
    var raw = String(text || '').replace(/[^0-9.,]/g, '');
    var lastDot = raw.lastIndexOf('.');
    var lastComma = raw.lastIndexOf(',');
    var cut = Math.max(lastDot, lastComma);
    var head = cut > -1 && raw.length - cut - 1 === 2 ? raw.slice(0, cut) : raw;
    if (head.indexOf(',') > -1) return ',';
    if (head.indexOf('.') > -1) return '.';
    return '';
  }

  function group(whole, sep) {
    if (!sep) return String(whole);
    return String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  /* Replace the numeric run inside the target's existing text, keeping the
     currency symbol, spacing and position exactly as Liquid rendered them. */
  function writeFigure(el, whole, sep) {
    var current = el.getAttribute('data-aurmina-format') || el.textContent;
    if (!el.getAttribute('data-aurmina-format')) {
      el.setAttribute('data-aurmina-format', current);
    }
    if (!/[0-9]/.test(current)) {
      el.textContent = group(whole, sep);
      return;
    }
    el.textContent = current.replace(/[0-9][0-9.,]*/, group(whole, sep));
  }

  function update(container) {
    var priceEl = container.querySelector('.price');
    if (!priceEl) return;

    var unit = parseMoney(priceEl.textContent);
    if (unit === null || unit <= 0) return;

    var months = parseFloat(container.getAttribute('data-aurmina-months'));
    if (isNaN(months) || months <= 0) return;

    var sep = groupChar(priceEl.textContent);

    /* Same arithmetic as the Liquid, rounded to whole units the same way. */
    var yearly = Math.round((unit * 12) / months);
    var monthly = Math.round(yearly / 12);

    var scope = container.closest('[data-aurmina-tile]') || container.parentNode;
    if (!scope) return;

    var yEl = scope.querySelector('[data-aurmina-yearly]');
    var mEl = scope.querySelector('[data-aurmina-monthly]');

    if (yEl) writeFigure(yEl, yearly, sep);
    if (mEl) writeFigure(mEl, monthly, sep);
  }

  function watch(container) {
    if (container.__aurminaWatched) return;
    container.__aurminaWatched = true;

    var timer = null;
    var settle = function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        update(container);
      }, SETTLE_MS);
    };

    if ('MutationObserver' in window) {
      var observer = new MutationObserver(settle);
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    /* Backstop for a rewrite that replaces the container wholesale, or an
       app that finishes before the observer is attached. */
    window.setTimeout(function () {
      update(container);
    }, BACKSTOP_MS);
  }

  function init(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-aurmina-derive]');
    for (var i = 0; i < nodes.length; i++) {
      watch(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
